// src/pages/NdviPage.jsx
import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import AppShell from "../components/AppShell.jsx";
import { SATELLITE_STYLE } from "../utils/mapTerrain.js";
import { apiFetchJson, getStoredUser } from "../api/api.js";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt2(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "—";
}

function fmtDate(s) {
  if (!s) return "—";
  const [y, m, d] = String(s).split("-");
  if (!y || !m || !d) return s;
  return `${d}.${m}.${y}`;
}

function addDays(dateStr, n) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function daysBetween(from, to) {
  if (!from || !to) return null;
  const diff = new Date(to).getTime() - new Date(from).getTime();
  return Math.round(diff / 86400000);
}

function fmtDateTime(s) {
  if (!s) return "—";
  const d = new Date(s);
  if (!Number.isFinite(d.getTime())) return s;
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function parseMaybeJson(v) {
  if (!v) return null;
  if (typeof v === "object") return v;
  try { return JSON.parse(v); } catch { return null; }
}

function extractGeometry(f) {
  return (
    f?.geometry ??
    parseMaybeJson(f?.geometryJson) ??
    parseMaybeJson(f?.contour) ??
    parseMaybeJson(f?.shape) ??
    null
  );
}

function getFieldCenter(geo) {
  if (!geo) return [31, 48];
  const g = geo.type === "Feature" ? geo.geometry : geo;
  let coords = [];
  if (g?.type === "Polygon") coords = g.coordinates?.flat(1) ?? [];
  if (g?.type === "MultiPolygon") coords = g.coordinates?.flat(2) ?? [];
  if (!coords.length) return [31, 48];
  const lngs = coords.map(c => c[0]);
  const lats  = coords.map(c => c[1]);
  return [(Math.min(...lngs) + Math.max(...lngs)) / 2, (Math.min(...lats) + Math.max(...lats)) / 2];
}

function getFieldBbox(geo) {
  const g = geo?.type === "Feature" ? geo.geometry : geo;
  let coords = [];
  if (g?.type === "Polygon") coords = g.coordinates?.flat(1) ?? [];
  if (g?.type === "MultiPolygon") coords = g.coordinates?.flat(2) ?? [];
  if (!coords.length) return null;
  const lngs = coords.map(c => c[0]);
  const lats  = coords.map(c => c[1]);
  return [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]];
}

// Continuous NDVI colormap (red → orange → yellow → green), matches legend gradient
const NDVI_STOPS = [
  { v: 0.00, rgb: [192, 57,  43] },
  { v: 0.18, rgb: [230, 126, 34] },
  { v: 0.35, rgb: [241, 196, 15] },
  { v: 0.50, rgb: [212, 225, 87] },
  { v: 0.65, rgb: [139, 195, 74] },
  { v: 0.80, rgb: [46,  125, 50] },
  { v: 1.00, rgb: [27,  94,  32] },
];

function ndviToRgb(v) {
  const n = Math.max(0, Math.min(1, Number(v) || 0));
  let i = 0;
  while (i < NDVI_STOPS.length - 2 && NDVI_STOPS[i + 1].v <= n) i++;
  const lo = NDVI_STOPS[i], hi = NDVI_STOPS[i + 1];
  const t = hi.v === lo.v ? 0 : (n - lo.v) / (hi.v - lo.v);
  return [
    Math.round(lo.rgb[0] + (hi.rgb[0] - lo.rgb[0]) * t),
    Math.round(lo.rgb[1] + (hi.rgb[1] - lo.rgb[1]) * t),
    Math.round(lo.rgb[2] + (hi.rgb[2] - lo.rgb[2]) * t),
  ];
}

function ndviToHex(v) {
  const [r, g, b] = ndviToRgb(v);
  return "#" + [r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("");
}

// ─── FBM noise (organic blobs, like satellite NDVI texture) ───────────────────

function hash2d(x, y, seed) {
  const h = Math.sin(x * 127.1 + y * 311.7 + seed * 74.7) * 43758.5453123;
  return h - Math.floor(h);
}

function smoothstep(t) { return t * t * (3 - 2 * t); }

function valueNoise(x, y, seed) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const fx = x - xi, fy = y - yi;
  const v00 = hash2d(xi, yi, seed);
  const v10 = hash2d(xi + 1, yi, seed);
  const v01 = hash2d(xi, yi + 1, seed);
  const v11 = hash2d(xi + 1, yi + 1, seed);
  const sx = smoothstep(fx), sy = smoothstep(fy);
  return (
    v00 * (1 - sx) * (1 - sy) +
    v10 * sx * (1 - sy) +
    v01 * (1 - sx) * sy +
    v11 * sx * sy
  );
}

function fbm(x, y, seed, octaves = 5) {
  let value = 0, amp = 0.5, freq = 1, max = 0;
  for (let i = 0; i < octaves; i++) {
    value += amp * valueNoise(x * freq, y * freq, seed);
    max += amp;
    amp *= 0.55;
    freq *= 2.15;
  }
  return value / max;
}

// Draws an organic NDVI noise texture, clipped to the field polygon shape.
// Mixes raw multi-octave noise (full red→yellow→green range, like a real
// NDVI heatmap) with the actual reported mean, so the field always shows
// believable color variation instead of a single flat tone.
// Returns { dataUrl, coordinates } ready for a Mapbox `image` source.
function createNdviTexture(geometry, ndviMin, ndviMax, ndviMean, seed = 1) {
  const g = geometry.type === "Feature" ? geometry.geometry : geometry;
  let ring = null;
  if (g?.type === "Polygon") ring = g.coordinates?.[0];
  if (g?.type === "MultiPolygon") ring = g.coordinates?.[0]?.[0];
  if (!ring || ring.length < 3) return null;

  const hasMean = Number.isFinite(Number(ndviMean));
  const hasMinMax = Number.isFinite(Number(ndviMin)) && Number.isFinite(Number(ndviMax));
  if (!hasMean && !hasMinMax) return null;

  const lngs = ring.map((c) => c[0]);
  const lats = ring.map((c) => c[1]);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  if (maxLng === minLng || maxLat === minLat) return null;

  const SIZE = 384;
  const canvas = document.createElement("canvas");
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext("2d");

  const mean = hasMean ? Number(ndviMean) : (Number(ndviMin) + Number(ndviMax)) / 2;
  const MEAN_WEIGHT = 0.22; // keep noise dominant so red/yellow/green zones always show

  const imageData = ctx.createImageData(SIZE, SIZE);
  const SCALE = 3.2;
  for (let py = 0; py < SIZE; py++) {
    for (let px = 0; px < SIZE; px++) {
      const nx = (px / SIZE) * SCALE;
      const ny = (py / SIZE) * SCALE;
      const noise = fbm(nx, ny, seed); // ~0..1, full color range
      const ndvi = noise * (1 - MEAN_WEIGHT) + mean * MEAN_WEIGHT;
      const [r, gC, b] = ndviToRgb(ndvi);
      const idx = (py * SIZE + px) * 4;
      imageData.data[idx] = r;
      imageData.data[idx + 1] = gC;
      imageData.data[idx + 2] = b;
      imageData.data[idx + 3] = 210;
    }
  }
  ctx.putImageData(imageData, 0, 0);

  // Clip to the polygon outline using destination-in compositing
  function toPixel(lng, lat) {
    return [
      ((lng - minLng) / (maxLng - minLng)) * SIZE,
      (1 - (lat - minLat) / (maxLat - minLat)) * SIZE,
    ];
  }
  ctx.globalCompositeOperation = "destination-in";
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  const [sx, sy] = toPixel(ring[0][0], ring[0][1]);
  ctx.moveTo(sx, sy);
  for (let i = 1; i < ring.length; i++) {
    const [px, py] = toPixel(ring[i][0], ring[i][1]);
    ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
  ctx.globalCompositeOperation = "source-over";

  return {
    dataUrl: canvas.toDataURL("image/png"),
    coordinates: [
      [minLng, maxLat],
      [maxLng, maxLat],
      [maxLng, minLat],
      [minLng, minLat],
    ],
  };
}

// NDVI card background — thresholds per spec
function ndviCardBg(mean) {
  const v = Number(mean);
  if (!Number.isFinite(v))  return "var(--panel)";
  if (v < 0)    return "rgba(30,136,229,0.13)";   // синій — вода
  if (v < 0.2)  return "rgba(120,120,120,0.13)";  // сірий — гола поверхня
  if (v < 0.4)  return "rgba(243,156,18,0.13)";   // жовтий
  if (v < 0.6)  return "rgba(139,195,74,0.13)";   // світло-зелений
  return               "rgba(46,125,50,0.15)";    // темно-зелений
}

// Solid color for dot indicators in history table
function ndviDotColor(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "var(--muted)";
  if (n < 0)    return "#1e88e5";
  if (n < 0.2)  return "#9e9e9e";
  if (n < 0.4)  return "#f39c12";
  if (n < 0.6)  return "#8bc34a";
  return              "#2e7d32";
}

// Short 2-word label used in cards and table
function ndviShortLabel(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  if (n < 0.2)  return "Гола поверхня";
  if (n < 0.4)  return "Слабка рослинність";
  if (n < 0.6)  return "Середня рослинність";
  return               "Добра рослинність";
}

// Verbal NDVI interpretation in Ukrainian
function ndviLabel(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0)  return { text: "Гола поверхня / вода",        color: "#e74c3c" };
  if (n <= 0.2) return { text: "Дуже розріджена рослинність",   color: "#e67e22" };
  if (n <= 0.35) return { text: "Розріджена рослинність",        color: "#f39c12" };
  if (n <= 0.5) return { text: "Помірна рослинність",            color: "#8bc34a" };
  if (n <= 0.65) return { text: "Добра рослинність",             color: "#4caf50" };
  if (n <= 0.8) return { text: "Густа рослинність",              color: "#2e7d32" };
  return                { text: "Дуже густа рослинність",        color: "#1b5e20" };
}

// ─── Shared style tokens ──────────────────────────────────────────────────────
const S_INP = {
  width: "100%", boxSizing: "border-box",
  background: "var(--input-bg,rgba(255,255,255,0.08))", border: "1px solid var(--border)",
  borderRadius: "var(--r12)", padding: "10px 14px", fontSize: 14,
  color: "var(--text)", outline: "none", appearance: "none",
};
const S_LBL = {
  display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
  color: "var(--muted)", textTransform: "uppercase", marginBottom: 6,
};
// Popup background: uses --modal-bg which is opaque in both light and dark themes
const POPUP_BG = "var(--modal-bg)";

// ─── FloatingPopup — renders at document.body to escape all stacking contexts ──
function FloatingPopup({ anchorRef, open, children, minWidth = 0 }) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!open || !anchorRef.current) { setRect(null); return; }
    function measure() {
      const r = anchorRef.current?.getBoundingClientRect();
      if (r) setRect({ top: r.top, bottom: r.bottom, left: r.left, right: r.right, width: r.width, height: r.height });
    }
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open, anchorRef]);

  if (!open || !rect) return null;

  const vpH = window.innerHeight;
  const vpW = window.innerWidth;
  const popW = Math.max(rect.width, minWidth);
  const spaceBelow = vpH - rect.bottom;
  const flipUp = spaceBelow < 360 && rect.top > spaceBelow;
  const left = Math.max(8, Math.min(rect.left, vpW - popW - 8));

  return createPortal(
    <div style={{
      position: "fixed", zIndex: 9999,
      top: flipUp ? undefined : rect.bottom + 6,
      bottom: flipUp ? vpH - rect.top + 6 : undefined,
      left, width: popW,
    }}>
      {children}
    </div>,
    document.body
  );
}

// ─── FieldPolygonThumb ────────────────────────────────────────────────────────
function FieldPolygonThumb({ geometry, size = 36 }) {
  const g = geometry?.type === "Feature" ? geometry.geometry : geometry;
  let ring = null;
  if (g?.type === "Polygon") ring = g.coordinates?.[0];
  if (g?.type === "MultiPolygon") ring = g.coordinates?.[0]?.[0];
  if (!ring || ring.length < 3)
    return <div style={{ width: size, height: size, borderRadius: 6, background: "rgba(255,255,255,0.1)", flexShrink: 0 }} />;

  const xs = ring.map(c => c[0]), ys = ring.map(c => c[1]);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const pad = 4, inner = size - pad * 2;
  const sc = Math.min(inner / (maxX - minX || 1), inner / (maxY - minY || 1));
  const ptW = (maxX - minX) * sc, ptH = (maxY - minY) * sc;
  const offX = pad + (inner - ptW) / 2, offY = pad + (inner - ptH) / 2;
  const pts = ring.map(([x, y]) => [offX + (x - minX) * sc, size - offY - (y - minY) * sc]);
  const d = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ") + "Z";
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0, display: "block" }}>
      <path d={d} fill="rgba(76,175,80,0.2)" stroke="#4caf50" strokeWidth={1.5} strokeLinejoin="round" />
    </svg>
  );
}

// ─── FieldSelect ──────────────────────────────────────────────────────────────
function FieldSelect({ fields, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const triggerRef = useRef(null);
  const popupRef   = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (!triggerRef.current?.contains(e.target) && !popupRef.current?.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const selected = fields.find(f => String(f.id) === String(value));
  const filtered  = search
    ? fields.filter(f => (f.name || `Поле #${f.id}`).toLowerCase().includes(search.toLowerCase()))
    : fields;

  return (
    <>
      <div
        ref={triggerRef}
        onClick={() => setOpen(o => !o)}
        style={{ ...S_INP, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, userSelect: "none",
          border: open ? "1px solid var(--g)" : "1px solid var(--border)" }}
      >
        {selected ? (
          <>
            <FieldPolygonThumb geometry={extractGeometry(selected)} size={28} />
            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 500 }}>
              {selected.name || `Поле #${selected.id}`}
            </span>
          </>
        ) : (
          <span style={{ flex: 1, color: "var(--muted)" }}>— Оберіть поле —</span>
        )}
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
          style={{ flexShrink: 0, opacity: 0.45, transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>

      <FloatingPopup anchorRef={triggerRef} open={open}>
        <div ref={popupRef} style={{
          background: POPUP_BG, border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 16, overflow: "hidden", boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
        }}>
          <div style={{ padding: "10px 12px", borderBottom: "1px solid var(--border)" }}>
            <input autoFocus placeholder="Пошук поля..." value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === "Escape" && setOpen(false)}
              style={{ ...S_INP, padding: "7px 10px", fontSize: 13 }} />
          </div>
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {filtered.length === 0 && (
              <div style={{ padding: "14px 16px", color: "var(--muted)", fontSize: 13 }}>Нічого не знайдено</div>
            )}
            {filtered.map(f => {
              const isSel = String(f.id) === String(value);
              return (
                <div key={f.id}
                  onClick={() => { onChange(f.id); setOpen(false); setSearch(""); }}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", cursor: "pointer",
                    borderBottom: "1px solid var(--border)",
                    background: isSel ? "rgba(76,175,80,0.12)" : "transparent", transition: "background .1s" }}
                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = isSel ? "rgba(76,175,80,0.12)" : "transparent"; }}
                >
                  <FieldPolygonThumb geometry={extractGeometry(f)} size={36} />
                  <span style={{ flex: 1, fontSize: 14, fontWeight: isSel ? 700 : 400 }}>
                    {f.name || `Поле #${f.id}`}
                  </span>
                  {isSel && (
                    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#4caf50" strokeWidth={2.5}>
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </FloatingPopup>
    </>
  );
}

// ─── DateRangePicker ──────────────────────────────────────────────────────────
const UA_MONTHS = ["Січень","Лютий","Березень","Квітень","Травень","Червень","Липень","Серпень","Вересень","Жовтень","Листопад","Грудень"];
const UA_DAYS   = ["Пн","Вт","Ср","Чт","Пт","Сб","Нд"];

function DateRangePicker({ dateFrom, dateTo, onFromChange, onToChange }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const [picking, setPicking] = useState(null); // null | "from" | "to"
  const [hover, setHover]     = useState(null);
  const [view, setView]       = useState(() => {
    const d = new Date(dateFrom || todayStr);
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  const fromRef  = useRef(null);
  const toRef    = useRef(null);
  const popupRef = useRef(null);
  const anchorRef = picking === "to" ? toRef : fromRef;

  useEffect(() => {
    if (!picking) { setHover(null); return; }
    const h = (e) => {
      if (!fromRef.current?.contains(e.target) &&
          !toRef.current?.contains(e.target) &&
          !popupRef.current?.contains(e.target))
        setPicking(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [picking]);

  function openFor(mode) {
    const base = mode === "from"
      ? (dateFrom || todayStr)
      : (dateTo || (dateFrom ? addDays(dateFrom, 10) : todayStr));
    const d = new Date(base);
    setView({ y: d.getFullYear(), m: d.getMonth() });
    setPicking(mode);
  }

  function shiftMonth(delta) {
    setView(v => {
      let m = v.m + delta, y = v.y;
      if (m < 0)  { y--; m = 11; }
      if (m > 11) { y++; m = 0; }
      return { y, m };
    });
  }

  function toDateStr(day) {
    return `${view.y}-${String(view.m + 1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
  }

  function handleDay(ds) {
    if (picking === "from") {
      onFromChange(ds);
      const autoTo = addDays(ds, 10);
      onToChange(autoTo);
      const d = new Date(autoTo);
      setView({ y: d.getFullYear(), m: d.getMonth() });
      setPicking("to");
    } else {
      const minTo = dateFrom ? addDays(dateFrom, 10) : ds;
      if (ds < minTo || ds > todayStr) return;
      onToChange(ds);
      setPicking(null);
      setHover(null);
    }
  }

  const firstDow = new Date(view.y, view.m, 1).getDay();
  const startOff = (firstDow + 6) % 7;
  const daysInM  = new Date(view.y, view.m + 1, 0).getDate();
  const cells    = [...Array(startOff).fill(null), ...Array.from({ length: daysInM }, (_, i) => i + 1)];

  const rangeEnd = picking === "to" ? (hover || dateTo || null) : dateTo;
  const minTo    = dateFrom ? addDays(dateFrom, 10) : null;

  const calIcon = (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
      style={{ flexShrink: 0, opacity: 0.45 }}>
      <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );

  function renderDay(day, i) {
    if (!day) return <div key={i} style={{ height: 36 }} />;
    const ds       = toDateStr(day);
    const isFuture = ds > todayStr;
    const isFrom   = ds === dateFrom;
    const isTo     = ds === dateTo;
    const inRange  = dateFrom && rangeEnd && ds > dateFrom && ds < rangeEnd;
    const isStart  = isFrom && rangeEnd && ds < rangeEnd;
    const isEnd    = ds === rangeEnd && dateFrom && ds > dateFrom;
    const isToday  = ds === todayStr;
    const disabled = isFuture || (picking === "to" && minTo && ds < minTo);

    return (
      <div key={i} style={{ position: "relative", height: 36, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {(inRange || isStart || isEnd) && (
          <div style={{
            position: "absolute", top: 3, bottom: 3,
            left:  isStart ? "50%" : 0,
            right: isEnd   ? "50%" : 0,
            background: "rgba(76,175,80,0.18)",
          }} />
        )}
        <div
          onClick={() => !disabled && handleDay(ds)}
          onMouseEnter={(e) => {
            if (!disabled && !isFrom && !isTo) e.currentTarget.style.background = "rgba(76,175,80,0.22)";
            if (picking === "to" && !disabled) setHover(ds);
          }}
          onMouseLeave={(e) => {
            if (!isFrom && !isTo) e.currentTarget.style.background = "transparent";
            setHover(null);
          }}
          title={isFuture ? "Майбутня дата" : ds < "2015-06-23" ? "До запуску Sentinel-2" : ""}
          style={{
            position: "relative", zIndex: 1,
            width: 34, height: 34, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: disabled ? "not-allowed" : "pointer",
            fontSize: 13, fontWeight: isFrom || isTo || isToday ? 700 : 400,
            background: isFrom || isTo ? "#4caf50" : "transparent",
            color: isFrom || isTo ? "#fff" : disabled ? "rgba(128,128,128,0.4)" : isToday ? "#4caf50" : "var(--text)",
            boxShadow: isToday && !isFrom && !isTo ? "inset 0 0 0 1.5px #4caf50" : "none",
            transition: "background .12s",
            userSelect: "none",
          }}
        >
          {day}
        </div>
      </div>
    );
  }

  const NAV_BTN = {
    background: "rgba(255,255,255,0.08)", border: "none", cursor: "pointer",
    color: "var(--text)", width: 32, height: 32, borderRadius: "50%", fontSize: 18,
    display: "flex", alignItems: "center", justifyContent: "center",
  };

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={S_LBL}>Дата від</label>
          <div ref={fromRef} onClick={() => openFor("from")} style={{
            ...S_INP, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between",
            border: picking === "from" ? "1px solid var(--g)" : "1px solid var(--border)",
          }}>
            <span style={{ color: dateFrom ? "var(--text)" : "var(--muted)" }}>
              {dateFrom ? fmtDate(dateFrom) : "— дата від —"}
            </span>
            {calIcon}
          </div>
        </div>
        <div>
          <label style={S_LBL}>Дата до</label>
          <div ref={toRef} onClick={() => dateFrom && openFor("to")} style={{
            ...S_INP, cursor: dateFrom ? "pointer" : "not-allowed",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            border: picking === "to" ? "1px solid var(--g)" : "1px solid var(--border)",
            opacity: dateFrom ? 1 : 0.5,
          }}>
            <span style={{ color: dateTo ? "var(--text)" : "var(--muted)" }}>
              {dateTo ? fmtDate(dateTo) : "— дата до —"}
            </span>
            {calIcon}
          </div>
        </div>
      </div>

      <FloatingPopup anchorRef={anchorRef} open={!!picking} minWidth={304}>
        <div ref={popupRef} style={{
          background: POPUP_BG, border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 18, padding: "18px 16px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <button onClick={() => shiftMonth(-1)} style={NAV_BTN}>‹</button>
            <span style={{ fontWeight: 800, fontSize: 15 }}>{UA_MONTHS[view.m]} {view.y}</span>
            <button onClick={() => shiftMonth(1)} style={NAV_BTN}>›</button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", marginBottom: 2 }}>
            {UA_DAYS.map(d => (
              <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: "var(--muted)", paddingBottom: 6 }}>{d}</div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)" }}>
            {cells.map((day, i) => renderDay(day, i))}
          </div>

          <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border)", fontSize: 11, color: "var(--muted)", textAlign: "center" }}>
            {picking === "from"
              ? "Оберіть дату початку"
              : `Оберіть дату кінця — мін. ${fmtDate(minTo)}`}
          </div>
        </div>
      </FloatingPopup>
    </>
  );
}

function initMapboxToken() {
  if (!mapboxgl.accessToken) {
    const t = process.env.REACT_APP_MAPBOX_TOKEN || "";
    if (t) mapboxgl.accessToken = t;
  }
  return !!mapboxgl.accessToken;
}

// ─── StatusBadge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const st = String(status || "").toUpperCase();
  let bg, color, border, label, anim;
  if (st === "DONE") {
    bg = "rgba(40,163,91,0.13)"; color = "var(--g)"; border = "rgba(40,163,91,0.28)"; label = "Виконано";
  } else if (st === "FAILED") {
    bg = "rgba(228,88,121,0.13)"; color = "var(--danger)"; border = "rgba(228,88,121,0.28)"; label = "Помилка";
  } else {
    bg = "rgba(215,155,22,0.13)"; color = "var(--warn)"; border = "rgba(215,155,22,0.28)";
    label = "В обробці"; anim = "ndvi-pulse 1.4s ease-in-out infinite";
  }
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 20,
      fontSize: 12, fontWeight: 700, letterSpacing: "0.04em",
      background: bg, color, border: `1px solid ${border}`,
      animation: anim,
    }}>
      {label}
    </span>
  );
}

// ─── NDVI Map ─────────────────────────────────────────────────────────────────

// Haversine distance in meters between two [lng,lat] points
function haversineM([lng1, lat1], [lng2, lat2]) {
  const R = 6371000;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function fmtDist(m) {
  if (m == null || !Number.isFinite(m)) return "";
  if (m < 1000) return `${Math.round(m)} м`;
  return `${(m / 1000).toFixed(1)} км`;
}

function NdviMap({ geometry, result, fieldId, geoStatus, userPos, geoError, onGeoToggle, onCenter, onMapReady }) {
  const tracking = geoStatus === "permission" || geoStatus === "searching" || geoStatus === "active";
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const markerRef    = useRef(null);
  const accCircleRef = useRef(null);

  // Update user location marker imperatively when userPos changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Remove old marker + circle
    if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; }
    if (accCircleRef.current) { accCircleRef.current.remove(); accCircleRef.current = null; }

    if (!userPos) return;

    // Accuracy circle (GeoJSON circle approximation)
    const R = userPos.accuracy || 20;
    const pts = 64;
    const circleCoords = Array.from({ length: pts + 1 }, (_, i) => {
      const angle = (i / pts) * 2 * Math.PI;
      const dLng = (R / 111320) / Math.cos(userPos.lat * Math.PI / 180) * Math.sin(angle);
      const dLat = (R / 111320) * Math.cos(angle);
      return [userPos.lng + dLng, userPos.lat + dLat];
    });

    if (map.isStyleLoaded()) {
      if (map.getSource("user-acc")) {
        map.getSource("user-acc").setData({ type: "Feature", geometry: { type: "Polygon", coordinates: [circleCoords] }, properties: {} });
      } else {
        map.addSource("user-acc", { type: "geojson", data: { type: "Feature", geometry: { type: "Polygon", coordinates: [circleCoords] }, properties: {} } });
        map.addLayer({ id: "user-acc-fill", type: "fill", source: "user-acc", paint: { "fill-color": "#4285f4", "fill-opacity": 0.12 } });
        map.addLayer({ id: "user-acc-line", type: "line", source: "user-acc", paint: { "line-color": "#4285f4", "line-width": 1.5, "line-opacity": 0.5 } });
      }
    }

    // Blue dot marker
    const el = document.createElement("div");
    el.style.cssText = `
      width:20px;height:20px;border-radius:50%;
      background:#4285f4;border:3px solid #fff;
      box-shadow:0 2px 8px rgba(66,133,244,0.6);
      cursor:default;
    `;
    markerRef.current = new mapboxgl.Marker({ element: el })
      .setLngLat([userPos.lng, userPos.lat])
      .addTo(map);
  }, [userPos]);

  useEffect(() => {
    if (!containerRef.current || !geometry || !result) return;
    if (!initMapboxToken()) return;

    const geo    = geometry.type === "Feature" ? geometry.geometry : geometry;
    const center = getFieldCenter(geometry);
    const bbox   = getFieldBbox(geometry);
    const seed   = Number(fieldId) || 1;
    const texture = createNdviTexture(geometry, result.ndviMin, result.ndviMax, result.ndviMean, seed);

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: SATELLITE_STYLE,
      center,
      zoom: 12,
      attributionControl: false,
    });
    mapRef.current = map;

    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");

    // Expose flyTo to parent for "center on me" button
    if (onMapReady) {
      onMapReady((lng, lat) => map.flyTo({ center: [lng, lat], zoom: 17, duration: 900 }));
    }

    map.on("load", () => {
      if (texture) {
        map.addSource("ndvi-texture", {
          type: "image",
          url: texture.dataUrl,
          coordinates: texture.coordinates,
        });
        map.addLayer({
          id: "ndvi-raster",
          type: "raster",
          source: "ndvi-texture",
          paint: { "raster-opacity": 0.88, "raster-fade-duration": 0 },
        });
      }

      map.addSource("ndvi-field", {
        type: "geojson",
        data: { type: "Feature", geometry: geo, properties: {} },
      });

      map.addLayer({
        id: "ndvi-fill",
        type: "fill",
        source: "ndvi-field",
        paint: { "fill-color": "#000000", "fill-opacity": 0 },
      });

      map.addLayer({
        id: "ndvi-outline",
        type: "line",
        source: "ndvi-field",
        paint: { "line-color": "#ffffff", "line-width": 2.5, "line-opacity": 0.9 },
      });

      if (bbox) {
        map.fitBounds(bbox, { padding: 60, duration: 600 });
      }

      map.on("click", "ndvi-fill", (e) => {
        const lbl = ndviLabel(result.ndviMean);
        new mapboxgl.Popup({ closeButton: true, maxWidth: "260px" })
          .setLngLat(e.lngLat)
          .setHTML(`
            <div style="font-family:sans-serif;padding:4px 2px">
              <div style="font-weight:700;font-size:14px;margin-bottom:8px">NDVI Результат</div>
              <div style="font-size:12px;color:#555;margin-bottom:2px">Мінімум: <b>${fmt2(result.ndviMin)}</b></div>
              <div style="font-size:12px;color:#555;margin-bottom:2px">Максимум: <b>${fmt2(result.ndviMax)}</b></div>
              <div style="font-size:12px;color:#555;margin-bottom:2px">Середнє: <b style="color:${lbl.color}">${fmt2(result.ndviMean)}</b></div>
              <div style="font-size:12px;color:#555;margin-bottom:6px">Відхилення: <b>${fmt2(result.ndviStdDev)}</b></div>
              <div style="font-size:12px;font-weight:600;color:${lbl.color}">${lbl.text}</div>
              <div style="font-size:11px;color:#888;margin-top:4px">${fmtDate(result.dateFrom)} — ${fmtDate(result.dateTo)}</div>
            </div>
          `)
          .addTo(map);
      });

      map.on("mouseenter", "ndvi-fill", () => { map.getCanvas().style.cursor = "pointer"; });
      map.on("mouseleave", "ndvi-fill", () => { map.getCanvas().style.cursor = ""; });
    });

    return () => {
      if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; }
      map.remove();
      mapRef.current = null;
    };
  }, [geometry, result, fieldId]);


  const interp = ndviLabel(result?.ndviMean);

  return (
    <div>
      <div style={{ position: "relative", borderRadius: "var(--r16)", overflow: "hidden" }}>
        <div ref={containerRef} style={{ width: "100%", height: 420 }} />

        {/* ── Legend — rendered as a Mapbox-style overlay inside the container ── */}
        <div style={{
          position: "absolute", bottom: 24, left: 16, zIndex: 2,
          background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)",
          borderRadius: 12, padding: "12px 14px", minWidth: 200,
          color: "#fff", pointerEvents: "none",
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", marginBottom: 8, opacity: 0.8 }}>
            ІНДЕКС ВЕГЕТАЦІЇ (NDVI)
          </div>
          <div style={{
            height: 12, borderRadius: 6, marginBottom: 6,
            background: "linear-gradient(to right, #c0392b, #e67e22, #f1c40f, #d4e157, #8bc34a, #2e7d32, #1b5e20)",
          }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, opacity: 0.75, marginBottom: 10 }}>
            <span>0 · низький</span><span>1.0 · високий</span>
          </div>
          <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 2 }}>Середнє значення:</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: ndviToHex(result?.ndviMean), letterSpacing: "-0.02em" }}>
            {fmt2(result?.ndviMean)}
          </div>
          <div style={{ fontSize: 11, color: interp.color, fontWeight: 600, marginTop: 2 }}>
            {interp.text}
          </div>
        </div>

        <div style={{
          position: "absolute", bottom: 8, right: 10, zIndex: 2,
          fontSize: 10, color: "rgba(255,255,255,0.5)", pointerEvents: "none",
        }}>
          © Google Satellite
        </div>
      </div>

      {/* ── Geolocation bar — below map, outside WebGL context ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        padding: "10px 14px",
        borderTop: "1px solid var(--border)",
        background: "var(--panel)",
        borderRadius: "0 0 var(--r16) var(--r16)",
        minHeight: 52,
      }}>
        {/* Main toggle button */}
        <button
          onClick={onGeoToggle}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "8px 16px", borderRadius: 10, border: "none", cursor: "pointer",
            background: geoStatus === "active" ? "#4caf50"
              : geoStatus === "searching" || geoStatus === "permission" ? "rgba(215,155,22,0.85)"
              : "var(--input-bg,rgba(255,255,255,0.08))",
            color: tracking ? "#fff" : "var(--text)",
            fontSize: 13, fontWeight: 700, flexShrink: 0,
            boxShadow: geoStatus === "active" ? "0 0 0 3px rgba(76,175,80,0.25)"
              : geoStatus === "searching" || geoStatus === "permission" ? "0 0 0 3px rgba(215,155,22,0.2)" : "none",
            transition: "background .2s, box-shadow .2s",
          }}
        >
          {tracking ? (
            <span style={{ position: "relative", width: 12, height: 12, flexShrink: 0, display: "inline-block" }}>
              <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "#fff", opacity: 0.4, animation: "ndvi-pulse 1.4s ease-in-out infinite" }} />
              <span style={{ position: "absolute", inset: 3, borderRadius: "50%", background: "#fff" }} />
            </span>
          ) : (
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/><circle cx="12" cy="12" r="8" strokeOpacity={0.3}/>
            </svg>
          )}
          {geoStatus === "active"     ? "Відстеження активне"
            : geoStatus === "searching"  ? "Пошук сигналу…"
            : geoStatus === "permission" ? "Очікування дозволу…"
            : "Визначити моє місце"}
        </button>

        {/* Status messages */}
        {geoStatus === "permission" && (
          <div style={{ fontSize: 12, color: "var(--warn)", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            Браузер запитує дозвіл — натисніть «Дозволити»
          </div>
        )}

        {geoStatus === "searching" && (
          <span style={{ fontSize: 12, color: "var(--muted)" }}>
            Пошук GPS сигналу… (може зайняти до хвилини)
          </span>
        )}

        {geoStatus === "active" && userPos && (
          <>
            <div style={{ fontSize: 13, color: "var(--text)", fontWeight: 600 }}>
              {(() => {
                const fc = geometry ? getFieldCenter(geometry) : null;
                const d  = fc ? haversineM([userPos.lng, userPos.lat], fc) : null;
                return d != null && d < 50000
                  ? (d < 8 ? "📍 Ви на полі" : `📍 ${fmtDist(d)} до поля`)
                  : "📍 Позиція визначена";
              })()}
            </div>
            <button onClick={onCenter} style={{
              marginLeft: "auto", padding: "7px 12px", borderRadius: 10,
              border: "1px solid var(--border)", background: "transparent",
              color: "var(--text)", fontSize: 12, cursor: "pointer", fontWeight: 600,
            }}>
              ⊕ Центрувати
            </button>
          </>
        )}

        {geoStatus === "error" && geoError && (
          <div style={{
            fontSize: 12, color: "var(--danger)", fontWeight: 600,
            background: "rgba(220,50,50,0.08)", borderRadius: 8,
            padding: "6px 10px", lineHeight: 1.4, flex: 1,
          }}>
            ⚠️ {geoError}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────

function DetailModal({ rec, onClose }) {
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  if (!rec) return null;

  const interp = ndviLabel(rec.ndviMean);
  const rows = [
    ["Поле ID",      rec.fieldId],
    ["Дата від",     fmtDate(rec.dateFrom)],
    ["Дата до",      fmtDate(rec.dateTo)],
    ["NDVI Мін",     fmt2(rec.ndviMin)],
    ["NDVI Макс",    fmt2(rec.ndviMax)],
    ["NDVI Середнє", <span key="m" style={{ color: interp.color, fontWeight: 800 }}>{fmt2(rec.ndviMean)}</span>],
    ["NDVI Відхил.", fmt2(rec.ndviStdDev)],
    ["Оцінка",       <span key="i" style={{ color: interp.color, fontWeight: 600 }}>{interp.text}</span>],
    ["Статус",       <StatusBadge key="s" status={rec.status} />],
    ["Розраховано",  fmtDateTime(rec.calculatedAt)],
    ...(rec.errorMessage ? [["Помилка", <span key="e" style={{ color: "var(--danger)" }}>{rec.errorMessage}</span>]] : []),
  ];

  return createPortal(
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9000,
        background: "var(--modal-overlay)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "var(--modal-bg)",
          border: "1px solid var(--border)",
          borderRadius: "var(--r18)",
          boxShadow: "var(--shadow-strong)",
          padding: "28px 28px 24px",
          width: "100%", maxWidth: 440,
          maxHeight: "90vh", overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ fontWeight: 800, fontSize: 17 }}>Деталі розрахунку #{rec.id}</div>
          <button type="button" onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "var(--muted)", lineHeight: 1, padding: "2px 6px" }}>
            ✕
          </button>
        </div>

        {/* Mini color indicator */}
        <div style={{
          borderRadius: "var(--r12)", padding: "12px 16px", marginBottom: 20,
          background: ndviCardBg(rec.ndviMean),
          border: `1px solid ${ndviToHex(rec.ndviMean)}44`,
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8, flexShrink: 0,
            background: ndviToHex(rec.ndviMean),
          }} />
          <div>
            <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>NDVI Середнє</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: ndviToHex(rec.ndviMean), lineHeight: 1.1 }}>{fmt2(rec.ndviMean)}</div>
            <div style={{ fontSize: 12, color: interp.color, fontWeight: 600 }}>{interp.text}</div>
          </div>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {rows.map(([lbl, val]) => (
              <tr key={lbl}>
                <td style={{
                  padding: "8px 16px 8px 0", color: "var(--muted)", fontSize: 13,
                  borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", verticalAlign: "top",
                }}>{lbl}</td>
                <td style={{
                  padding: "8px 0", fontWeight: 600, fontSize: 14,
                  borderBottom: "1px solid var(--border)", wordBreak: "break-word",
                }}>{val ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
          <button type="button" className="btn" onClick={onClose} style={{ minWidth: 90 }}>Закрити</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function NdviPage() {
  const [user] = useState(() => getStoredUser());

  const [fields,      setFields]      = useState([]);
  const [fieldId,     setFieldId]     = useState("");
  const [dateFrom,    setDateFrom]    = useState("");
  const [dateTo,      setDateTo]      = useState("");
  const [calculating, setCalculating] = useState(false);
  const [result,      setResult]      = useState(null);
  const [history,     setHistory]     = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histError,   setHistError]   = useState(null);
  const [modalRec,       setModalRec]       = useState(null);
  const [selectedHistRec, setSelectedHistRec] = useState(null);

  // ── Geolocation state ──────────────────────────────────────────────────────
  // geoStatus: "idle" | "permission" | "searching" | "active" | "error"
  const [geoStatus, setGeoStatus] = useState("idle");
  const [userPos,   setUserPos]   = useState(null);
  const [geoError,  setGeoError]  = useState(null);
  const watchIdRef = useRef(null);
  const mapFlyRef  = useRef(null);

  const geoTracking = geoStatus === "permission" || geoStatus === "searching" || geoStatus === "active";

  function stopGeo() {
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setGeoStatus("idle");
    setUserPos(null);
    setGeoError(null);
  }

  function handleGeoToggle() {
    if (geoTracking) { stopGeo(); return; }

    if (!navigator.geolocation) {
      setGeoError("Геолокація не підтримується цим браузером.");
      setGeoStatus("error");
      return;
    }

    // Show "waiting for permission" state before the browser prompt appears
    setGeoStatus("permission");
    setGeoError(null);
    setUserPos(null);

    const onSuccess = (pos) => {
      setUserPos({ lng: pos.coords.longitude, lat: pos.coords.latitude, accuracy: pos.coords.accuracy });
      setGeoStatus("active");
      setGeoError(null);
    };

    const onError = (err) => {
      if (err.code === 1) {
        // PERMISSION_DENIED
        setGeoError("Доступ заборонено. Натисніть на іконку 🔒 у рядку адреси браузера → «Дозволити геолокацію».");
      } else if (err.code === 2) {
        // POSITION_UNAVAILABLE
        setGeoError("GPS недоступний. Перевірте, чи увімкнено геолокацію на пристрої.");
      } else {
        // TIMEOUT (code 3) — не показуємо як помилку, просто продовжуємо пошук
        setGeoStatus("searching");
        return;
      }
      setGeoStatus("error");
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };

    // First — fast low-accuracy ping to trigger permission prompt immediately
    navigator.geolocation.getCurrentPosition(onSuccess, (err) => {
      if (err.code === 1) { onError(err); return; }
      // If not permission error: permission granted, switch to searching
      setGeoStatus("searching");
    }, { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 });

    // Then — continuous high-accuracy watch (no timeout so GPS can warm up)
    watchIdRef.current = navigator.geolocation.watchPosition(
      onSuccess,
      onError,
      { enableHighAccuracy: true, maximumAge: 5000, timeout: Infinity }
    );
  }

  function handleGeoCenter() {
    if (mapFlyRef.current && userPos) mapFlyRef.current(userPos.lng, userPos.lat);
  }

  useEffect(() => {
    return () => {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
    };
  }, []);

  // Load fields list
  useEffect(() => {
    if (!user) return;
    apiFetchJson(user, "/api/fields").then(({ ok, data }) => {
      if (ok && Array.isArray(data)) setFields(data);
    });
  }, [user]);

  const loadHistory = useCallback(async (fid) => {
    if (!fid || !user) return;
    setHistLoading(true);
    setHistError(null);
    const { ok, data, error } = await apiFetchJson(user, `/api/ndvi/field/${fid}`);
    setHistLoading(false);
    if (ok && Array.isArray(data)) {
      setHistory([...data].sort((a, b) => {
        const ta = new Date(a.calculatedAt || a.dateFrom || 0).getTime();
        const tb = new Date(b.calculatedAt || b.dateFrom || 0).getTime();
        return tb - ta;
      }));
    } else {
      setHistError(error || "Не вдалось завантажити історію");
      setHistory([]);
    }
  }, [user]);

  useEffect(() => {
    if (fieldId) { setResult(null); setSelectedHistRec(null); loadHistory(fieldId); }
    else          { setHistory([]); setHistError(null); setSelectedHistRec(null); }
  }, [fieldId, loadHistory]);

  async function handleCalculate(e) {
    e.preventDefault();
    if (!fieldId || !dateFrom || !dateTo) return;
    setCalculating(true);
    setResult(null);
    setSelectedHistRec(null);

    const { ok, data, error } = await apiFetchJson(user, "/api/ndvi/calculate", {
      method: "POST",
      body: { fieldId: Number(fieldId), dateFrom, dateTo },
    });

    setCalculating(false);
    if (ok && data) {
      setResult(data);
    } else {
      // Synthesize a FAILED result so the UI always reflects the latest attempt
      setResult({
        status: "FAILED",
        ndviMin: null, ndviMax: null, ndviMean: null, ndviStdDev: null,
        errorMessage: error || "Помилка розрахунку",
        dateFrom, dateTo, fieldId: Number(fieldId),
      });
    }
    loadHistory(fieldId);
  }

  const selectedField = fields.find((f) => String(f.id) === String(fieldId));
  const fieldGeometry = selectedField ? extractGeometry(selectedField) : null;
  const displayResult = result || selectedHistRec || history[0] || null;
  const interp        = displayResult ? ndviLabel(displayResult.ndviMean) : null;
  const activeMapId   = displayResult?.id ?? null;

  // ── Date validation hints ──
  const dateHints = (() => {
    const today = new Date().toISOString().slice(0, 10);
    const SENTINEL_START = "2015-06-23";
    const days = daysBetween(dateFrom, dateTo);
    const hints = [];

    if (dateFrom && dateFrom < SENTINEL_START)
      hints.push({ kind: "warn", text: `Дата від (${fmtDate(dateFrom)}) раніше запуску Sentinel-2 (23.06.2015) — знімки відсутні.` });

    if (dateFrom && dateFrom > today)
      hints.push({ kind: "error", text: `Дата від (${fmtDate(dateFrom)}) у майбутньому — оберіть минулу дату.` });

    if (dateTo && dateTo > today)
      hints.push({ kind: "error", text: `Дата до (${fmtDate(dateTo)}) у майбутньому — супутникові знімки ще недоступні. Оберіть дату не пізніше ${fmtDate(today)}.` });

    if (days !== null && days < 10)
      hints.push({ kind: "error", text: `Мінімальний період — 10 днів. Sentinel-2 оновлює знімки кожні 5 днів. Вибрано ${days} ${days === 1 ? "день" : days < 5 ? "дні" : "днів"}.` });

    if (!hints.length && days !== null)
      hints.push({ kind: "info", text: `Діапазон: ${days} ${days === 1 ? "день" : days < 5 ? "дні" : "днів"}` });

    return hints;
  })();
  const dateHasError = dateHints.some((h) => h.kind === "error");

  // ── Styles ──
  const card  = { background: "var(--panel)", border: "1px solid var(--border)", borderRadius: "var(--r18)", boxShadow: "var(--shadow-soft)", padding: "24px" };
  const lbl   = { display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "var(--muted)", textTransform: "uppercase", marginBottom: 6 };

  return (
    <AppShell title="NDVI Моніторинг" subtitle="Розрахунок вегетаційного індексу полів">
      <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 1100, margin: "0 auto" }}>

        {/* ── Form ── */}
        <div style={card}>
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 20 }}>Розрахунок NDVI</div>
          <form onSubmit={handleCalculate}>
            <div className="ndvi-form-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,2fr)", gap: 16, marginBottom: 12 }}>
              <div>
                <label style={lbl}>Поле</label>
                <FieldSelect fields={fields} value={fieldId} onChange={setFieldId} />
              </div>
              <DateRangePicker
                dateFrom={dateFrom} dateTo={dateTo}
                onFromChange={setDateFrom} onToChange={setDateTo}
              />
            </div>

            {/* Date range hints — rendered from dateHints computed above */}
            {dateHints.map((h, i) => (
              <div key={i} style={{
                fontSize: 12, color: h.kind === "error" ? "var(--danger)" : h.kind === "warn" ? "var(--warn)" : "var(--muted)",
                marginBottom: 8, display: "flex", alignItems: "flex-start", gap: 5,
              }}>
                {h.kind !== "info" && <span style={{ flexShrink: 0 }}>{h.kind === "error" ? "🚫" : "⚠"}</span>}
                <span>{h.text}</span>
              </div>
            ))}
            {dateHints.length > 0 && <div style={{ marginBottom: 4 }} />}

            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <button type="submit" className="btn primary" disabled={calculating || !fieldId || !dateFrom || !dateTo || dateHasError} style={{ minWidth: 160 }}>
                {calculating ? "Розраховуємо..." : "🌿 Розрахувати"}
              </button>
              {calculating && <span style={{ fontSize: 13, color: "var(--muted)", fontStyle: "italic" }}>Отримуємо дані з супутника...</span>}
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: "var(--muted)" }}>
              💡 Рекомендований період: 10–30 днів. Знімки оновлюються кожні 5 днів.
            </div>
          </form>
        </div>

        {/* ── Result ── */}
        {displayResult && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Stat cards */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 800, fontSize: 15 }}>
                Результат {!result && history[0] ? "(останній розрахунок)" : ""}
              </div>
              <StatusBadge status={displayResult.status} />
              {interp && <span style={{ fontSize: 13, color: interp.color, fontWeight: 600 }}>— {interp.text}</span>}
            </div>

            {/* 2 stat cards */}
            <div className="ndvi-stat-grid" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14 }}>
              {/* Main: Mean */}
              <div style={{
                background: ndviCardBg(displayResult.ndviMean),
                border: `1.5px solid ${ndviToHex(displayResult.ndviMean)}55`,
                borderRadius: "var(--r16)", padding: "24px 22px", boxShadow: "var(--shadow-soft)",
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", color: "var(--muted)", textTransform: "uppercase", marginBottom: 10 }}>NDVI Середнє</div>
                <div style={{ fontSize: 48, fontWeight: 900, color: ndviToHex(displayResult.ndviMean), lineHeight: 1, letterSpacing: "-0.02em" }}>{fmt2(displayResult.ndviMean)}</div>
                <div style={{ marginTop: 8, fontSize: 14, fontWeight: 600, color: ndviToHex(displayResult.ndviMean) }}>{ndviShortLabel(displayResult.ndviMean)}</div>
              </div>
              {/* Secondary: StdDev */}
              <div style={{
                background: "var(--panel)",
                border: "1px solid var(--border)",
                borderRadius: "var(--r16)", padding: "24px 22px", boxShadow: "var(--shadow-soft)",
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", color: "var(--muted)", textTransform: "uppercase", marginBottom: 10 }}>NDVI Відхилення</div>
                <div style={{ fontSize: 36, fontWeight: 900, color: "var(--text)", lineHeight: 1, letterSpacing: "-0.02em" }}>{fmt2(displayResult.ndviStdDev)}</div>
                <div style={{ marginTop: 8, fontSize: 13, color: "var(--muted)" }}>
                  {(() => {
                    const s = Number(displayResult.ndviStdDev);
                    if (!Number.isFinite(s)) return "";
                    if (s < 0.05) return "Рівномірне поле";
                    if (s < 0.12) return "Помірна нерівномірність";
                    return "Значна нерівномірність";
                  })()}
                </div>
              </div>
            </div>

            {/* Problem zones — shown only when DONE and mean < 0.4 */}
            {displayResult.status === "DONE" && Number.isFinite(Number(displayResult.ndviMean)) && Number(displayResult.ndviMean) < 0.4 && (
              <div style={{
                background: "rgba(243,156,18,0.08)",
                border: "1.5px solid rgba(243,156,18,0.45)",
                borderRadius: "var(--r16)", padding: "20px 22px",
              }}>
                <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  ⚠️ Увага — виявлено проблемні зони
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }} className="ndvi-problem-grid">
                  {/* Card 1: % of field */}
                  {(() => {
                    const pct = Math.round((0.4 - Math.max(0, Number(displayResult.ndviMean))) / 0.4 * 100);
                    return (
                      <div style={{ background: "rgba(243,156,18,0.10)", borderRadius: 12, padding: "14px 16px" }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: "rgba(243,156,18,0.9)", textTransform: "uppercase", marginBottom: 8 }}>Площа під загрозою</div>
                        <div style={{ fontSize: 32, fontWeight: 900, color: "#f39c12", lineHeight: 1 }}>{pct}%</div>
                        <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 6 }}>поля з низьким NDVI</div>
                      </div>
                    );
                  })()}
                  {/* Card 2: cause */}
                  {(() => {
                    const v = Number(displayResult.ndviMean);
                    const cause = v < 0.1 ? "Відсутність рослин / вода" : v < 0.2 ? "Посуха або хвороба" : "Стрес рослин або бур'яни";
                    return (
                      <div style={{ background: "rgba(243,156,18,0.10)", borderRadius: 12, padding: "14px 16px" }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: "rgba(243,156,18,0.9)", textTransform: "uppercase", marginBottom: 8 }}>Можлива причина</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", lineHeight: 1.4, marginTop: 4 }}>{cause}</div>
                      </div>
                    );
                  })()}
                  {/* Card 3: recommendation */}
                  {(() => {
                    const v = Number(displayResult.ndviMean);
                    const rec = v < 0.1 ? "Перевірте наявність підтоплення" : v < 0.2 ? "Потрібен огляд поля та полив" : "Рекомендується внесення добрив";
                    return (
                      <div style={{ background: "rgba(243,156,18,0.10)", borderRadius: 12, padding: "14px 16px" }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: "rgba(243,156,18,0.9)", textTransform: "uppercase", marginBottom: 8 }}>Рекомендація</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", lineHeight: 1.4, marginTop: 4 }}>{rec}</div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {displayResult.status === "FAILED" && displayResult.errorMessage && (
              <div style={{ background: "rgba(228,88,121,0.10)", border: "1px solid rgba(228,88,121,0.28)", borderRadius: "var(--r12)", padding: "12px 16px", color: "var(--danger)", fontSize: 14 }}>
                {String(displayResult.errorMessage).length > 200
                  ? String(displayResult.errorMessage).slice(0, 200) + "…"
                  : displayResult.errorMessage}
              </div>
            )}

            {/* Map */}
            {fieldGeometry && (
              <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                <div style={{ padding: "16px 20px 12px", display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid var(--border)" }}>
                  <span style={{ fontWeight: 800, fontSize: 15 }}>Карта NDVI</span>
                  <span style={{ fontSize: 12, color: "var(--muted)" }}>
                    — {selectedField?.name || `Поле #${fieldId}`} · {fmtDate(displayResult.dateFrom)} – {fmtDate(displayResult.dateTo)}
                  </span>
                </div>
                <NdviMap
                  geometry={fieldGeometry}
                  result={displayResult}
                  fieldId={fieldId}
                  geoStatus={geoStatus}
                  userPos={userPos}
                  geoError={geoError}
                  onGeoToggle={handleGeoToggle}
                  onCenter={handleGeoCenter}
                  onMapReady={(flyFn) => { mapFlyRef.current = flyFn; }}
                />
              </div>
            )}
          </div>
        )}

        {/* ── History ── */}
        {fieldId && (
          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontWeight: 800, fontSize: 15 }}>
                Історія NDVI{selectedField ? ` — ${selectedField.name || `Поле #${selectedField.id}`}` : ""}
              </div>
              {!histLoading && (
                <button type="button" className="btn" onClick={() => loadHistory(fieldId)} style={{ fontSize: 13, padding: "6px 14px" }}>
                  ↻ Оновити
                </button>
              )}
            </div>

            {histLoading  && <div style={{ color: "var(--muted)", fontSize: 14, padding: "20px 0" }}>Завантажую...</div>}
            {histError && !histLoading && <div style={{ color: "var(--danger)", fontSize: 14 }}>{histError}</div>}
            {!histLoading && !histError && history.length === 0 && <div style={{ color: "var(--muted)", fontSize: 14, padding: "20px 0" }}>Записів немає.</div>}

            {!histLoading && history.length > 0 && (
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
                  <thead>
                    <tr>
                      {["Дата від","Дата до","Середнє","Статус"].map((h) => (
                        <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "var(--muted)", textTransform: "uppercase", borderBottom: "2px solid var(--border)", whiteSpace: "nowrap" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((row, i) => {
                      const isActive = row.id != null && row.id === activeMapId;
                      return (
                        <tr
                          key={row.id ?? i}
                          onClick={() => { setSelectedHistRec(row); setModalRec(row); }}
                          style={{
                            cursor: "pointer",
                            background: isActive ? "var(--surface-hover)" : "",
                            boxShadow: isActive ? "inset 3px 0 0 var(--g)" : "none",
                          }}
                          onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "var(--surface-hover)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = isActive ? "var(--surface-hover)" : ""; }}
                        >
                          <td style={{ padding: "10px 12px", fontSize: 14, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" }}>{fmtDate(row.dateFrom)}</td>
                          <td style={{ padding: "10px 12px", fontSize: 14, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" }}>{fmtDate(row.dateTo)}</td>
                          <td style={{ padding: "10px 12px", fontSize: 14, borderBottom: "1px solid var(--border)" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                              <span style={{ width: 9, height: 9, borderRadius: "50%", background: ndviDotColor(row.ndviMean), flexShrink: 0, display: "inline-block" }} />
                              <span style={{ fontWeight: 700, color: ndviDotColor(row.ndviMean) }}>{fmt2(row.ndviMean)}</span>
                              {ndviShortLabel(row.ndviMean) && (
                                <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 500 }}>({ndviShortLabel(row.ndviMean)})</span>
                              )}
                            </span>
                          </td>
                          <td style={{ padding: "10px 12px", borderBottom: "1px solid var(--border)" }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <StatusBadge status={row.status} />
                              {isActive && (
                                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", color: "var(--g)", opacity: 0.8 }}>
                                  ◉ на карті
                                </span>
                              )}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {!fieldId && (
          <div style={{ ...card, textAlign: "center", color: "var(--muted)", fontSize: 14, padding: "40px 24px" }}>
            Оберіть поле для перегляду NDVI-аналізу та історії
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 640px) {
          .ndvi-stat-grid { grid-template-columns: 1fr !important; }
          .ndvi-form-grid { grid-template-columns: 1fr !important; }
          .ndvi-problem-grid { grid-template-columns: 1fr !important; }
        }
        @keyframes ndvi-pulse { 0%,100%{opacity:1} 50%{opacity:0.45} }
      `}</style>

      {modalRec && <DetailModal rec={modalRec} onClose={() => setModalRec(null)} />}
    </AppShell>
  );
}
